import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiService, CommissionFilters } from "../services/api";
import toast from "react-hot-toast";

export const useCommissions = (params?: CommissionFilters) => {
  return useQuery({
    queryKey: ["commissions", params],
    queryFn: () => apiService.getCommissionPayouts(params),
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 1,
    retryDelay: 1000,
  });
};

export const useCommissionStats = () => {
  return useQuery({
    queryKey: ["commissionStats"],
    queryFn: () => apiService.getCommissionStats(),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    retryDelay: 1000,
  });
};

export const useCommissionExportHistory = (limit?: number) => {
  return useQuery({
    queryKey: ["commissionExportHistory", limit],
    queryFn: () => apiService.getCommissionExportHistory(limit),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
};

export const useBulkExportCommissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (commissionPayoutIds: string[]) =>
      apiService.bulkExportCommissions(commissionPayoutIds),
    onSuccess: (data) => {
      if (data.excelBuffer) {
        const byteCharacters = atob(data.excelBuffer);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `commissions-export-${data.batchId}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
      toast.success(
        `Exported ${data.count} commission payouts (€${data.totalAmount.toFixed(2)})`,
      );
      queryClient.invalidateQueries({ queryKey: ["commissions"] });
      queryClient.invalidateQueries({ queryKey: ["commissionStats"] });
      queryClient.invalidateQueries({ queryKey: ["commissionExportHistory"] });
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || "Failed to export commission payouts",
      );
    },
  });
};

export const useBulkRejectCommissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      commissionPayoutIds,
      rejectionReason,
    }: {
      commissionPayoutIds: string[];
      rejectionReason: string;
    }) =>
      apiService.bulkRejectCommissions(commissionPayoutIds, rejectionReason),
    onSuccess: (data) => {
      toast.success(
        `Rejected ${data.data?.updatedCount ?? 0} commission payouts`,
      );
      queryClient.invalidateQueries({ queryKey: ["commissions"] });
      queryClient.invalidateQueries({ queryKey: ["commissionStats"] });
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || "Failed to reject commission payouts",
      );
    },
  });
};

export const useBulkMarkCommissionsPaid = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (commissionPayoutIds: string[]) =>
      apiService.bulkMarkCommissionsPaid(commissionPayoutIds),
    onSuccess: (data) => {
      toast.success(
        `Marked ${data.data?.updatedCount ?? 0} commission payouts as paid`,
      );
      queryClient.invalidateQueries({ queryKey: ["commissions"] });
      queryClient.invalidateQueries({ queryKey: ["commissionStats"] });
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message ||
          "Failed to mark commission payouts as paid",
      );
    },
  });
};
